<template>
  <div class="global-upload-section global-upload-section--default">
    <div class="upload-section__heading-wrapper">
      <div class="hide-mobile">
        <div :class="errorClass">
          <span class="icon icon--upload-error" aria-label="Upload Error"></span>
          {{ errorMessage }}
        </div>
      </div>
    </div>
    <teleport to="#pinnedUpload">
      <div v-if="uploadedArtwork && uploadedArtwork.artwork" class="upload-remove" @click="handleRemoveArtwork">
        Remove Photo
      </div>
      <ArtworkUploadButton
        v-else
        ref="ArtworkUploadButtonPinned"
        :title="buttonText"
        class="upload-button"
        :joineryHost="joineryHost"
        @upload-complete="handleUploadComplete($event)"
        @upload-error="handleUploadError($event)"
        @upload-initiated="handleUploadInitiated($event)"
      />
    </teleport>
    <div ref="artworkContainer">
      <div v-if="uploadedArtwork && uploadedArtwork.artwork" class="uploaded-image-container">
        <div class="global-upload-section__uploaded-image">
          <img :src="uploadedArtwork.artwork.square.url" alt="Uploaded Artwork Thumbnail" />
          <button @click="handleRemoveArtwork" class="uploaded-image__remove-button">
            <span class="screenreader">Remove</span>
            <span class="icon icon--upload-remove" aria-label="Upload Remove"></span>
          </button>
        </div>
        <button class="change-image-button" @click="handleChangeImage">Change Image</button>
        <ArtworkUploadButton
          ref="ArtworkUploadButtonHidden"
          :title="buttonText"
          class="upload-button upload-button--hidden"
          :class="{ 'is-processing': isProcessing }"
          :joineryHost="joineryHost"
          @upload-complete="handleUploadComplete($event)"
          @upload-error="handleUploadError($event)"
          @upload-initiated="handleUploadInitiated($event)"
          @upload-progress="handleUploadProgress($event)"
        />
      </div>
      <ArtworkUploadButton
        v-else
        ref="ArtworkUploadButton"
        :title="buttonText"
        class="upload-button"
        :class="{ 'is-processing': isProcessing }"
        :joineryHost="joineryHost"
        @upload-complete="handleUploadComplete($event)"
        @upload-error="handleUploadError($event)"
        @upload-initiated="handleUploadInitiated($event)"
        @upload-progress="handleUploadProgress($event)"
      />
      <div class="hide-desktop">
        <div :class="errorClass">
          <span class="icon icon--upload-error" aria-label="Upload Error"></span>
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArtworkUploadButton from '@framebridge/toolbox/ArtworkUploadButton';

export default {
  name: 'GlobalUploadComponent',
  components: {
    ArtworkUploadButton,
  },

  data() {
    return {
      joineryHost: SDG?.Data?.joineryHost || 'https://staging.framebridge.com',
      headingText: '',
      errorMessage: '',
      errorClass: 'hide',
      isPinned: false,
      buttonText: 'Upload Photo',
      uploadedArtwork: {},
      isProcessing: false,
      uploadProgress: 0,
      defaultText: '',
      uploadedText: '',
    };
  },
  mounted() {
    this.uploadedArtwork = this.getLocalStorageItem('uploadedArtwork');

    // set the heading text
    this.setHeadingText();
  },
  methods: {
    initializeObserver() {
      const elementToObserve = this.$refs.artworkContainer;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.isPinned = false;
            } else {
              // Element is not in view and toolbar is pinned
              this.isPinned = true;
            }
          });
        },
        {
          root: null,
          threshold: 0.9,
          rootMargin: '-90px',
        }
      );

      // Start observing the element
      observer.observe(elementToObserve);
    },
    getLocalStorageItem(key) {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    },

    handleUploadComplete(data) {
      localStorage.setItem('uploadedArtwork', JSON.stringify(data));
      this.uploadedArtwork = data;
      this.headingText = this.uploadedText;

      const changeEvent = new CustomEvent('uploadItem', {
        detail: {
          added: true,
          removed: false,
        },
      });

      window.dispatchEvent(changeEvent);
      this.isProcessing = false;
      this.$forceUpdate();
    },
    handleUploadError(data) {
      this.errorMessage = `${data || 'Unknown error'}`;
      this.errorClass = 'upload-error';
      this.isProcessing = false;
    },
    handleUploadInitiated() {
      this.errorClass = 'hide';
      this.buttonText = 'Uploading...';
      this.isProcessing = true;
    },
    handleRemoveArtwork() {
      localStorage.removeItem('uploadedArtwork');
      localStorage.removeItem("currentFrameSpec");
      this.uploadedArtwork = null; // Reset the uploadedArtwork to null
      this.headingText = this.defaultText;

      const changeEvent = new CustomEvent('uploadItem', {
        detail: {
          added: false,
          removed: true,
        },
      });

      window.dispatchEvent(changeEvent);
      this.buttonText = 'Upload Photo';

      this.$forceUpdate();
    },
    handleChangeImage() {
      if (this.$refs.ArtworkUploadButtonHidden) {
        this.$refs.ArtworkUploadButtonHidden.triggerImageUpload();
      }
    },
    handleUploadProgress(data) {
      const { percentComplete } = data;
      const roundedPercentComplete = Math.round(percentComplete);
      this.uploadProgress = { percentComplete: roundedPercentComplete };
      this.headingText = `${roundedPercentComplete}% Uploaded`;
    },
    setHeadingText() {
      // Set default and uploaded text from attributes in mounted lifecycle
      this.defaultText =
        this.$el.parentElement.getAttribute('data-section-heading') ||
        'Framing a digital photo? Upload here to see it in all frames.';
      this.uploadedText =
        this.$el.parentElement.getAttribute('data-section-uploaded-heading') ||
        'Previewing your digital photo in all frames. Select a frame to get started.';

      // set the heading text
      if (this.uploadedArtwork && this.uploadedArtwork.artwork) {
        this.headingText = this.uploadedText;
      } else {
        this.headingText = this.defaultText;
      }
    },
  },
};
</script>

<style lang="scss">
.global-upload-section {
  padding: 1rem 0;
  .uploaded-image-container {
    align-items: center;
    display: flex;
    gap: 0.5rem;
    justify-content: center;

    .change-image-button {
      background: none;
      border: none;
      color: #000;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 700;
      letter-spacing: 0.0175rem;
      line-height: 1.25rem;
      padding: 0.5rem 1rem;
      text-decoration: underline;
      text-transform: capitalize;
      width: auto;
    }
  }

  .upload-button {
    button {
      align-items: center;
      background-color: #eee171;
      display: flex;
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 700;
      gap: 0.625rem;
      justify-content: center;
      letter-spacing: 0.28px;
      line-height: 1.25rem;
      padding: 0.875rem 3.125rem;
      text-transform: capitalize;
      width: 100%;
    }

    &--hidden {
      opacity: 0;
      pointer-events: none;
      position: absolute;
      visibility: hidden;
    }
  }

  .uploaded-image {
    &__remove-button {
      all: unset;
      position: absolute;
      right: -0.625rem;
      top: 0;
    }
  }

  &__uploaded-image {
    border: 0.0625rem solid #000;
    border-radius: 50%;
    height: 3.75rem;
    position: relative;
    width: 3.75rem;

    img {
      border-radius: 50%;
      max-width: 100%;
      width: 3.75rem;
    }

    &__remove-button {
      all: unset;
      position: absolute;
      right: -0.625rem;
      top: 0;
    }

    .icon {
      cursor: pointer;
    }
  }
}
</style>
